import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, MenuItem, TextField } from '@mui/material';
import { C, COMPACT_FIELD_SX } from './tokens';
import { getLocalizedText } from './formatters';
import { breakoutFiltersShape } from './shapes';

// Filter bar above the candidates table.
// All option values come from backend metadata; this component just renders them.
const BreakoutFiltersBar = ({
  t,
  language,
  metadata,
  filters,
  setFilters,
  getMetadataLabel,
  onApply,
  loading,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: 'repeat(2, minmax(0, 1fr))',
        sm: 'repeat(3, minmax(0, 1fr))',
        md: 'repeat(4, minmax(0, 1fr))',
        lg: 'minmax(220px, 1.45fr) repeat(6, minmax(110px, 1fr)) auto',
      },
      gap: 1,
      py: 0.5,
      borderBottom: `1px solid ${C.borderSoft}`,
      alignItems: 'stretch',
    }}
  >
    <TextField
      select
      label={t('breakoutCandidates.filters.preset')}
      size="small"
      value={filters.preset}
      onChange={(e) => setFilters((p) => ({ ...p, preset: e.target.value }))}
      slotProps={{ inputLabel: { shrink: true } }}
      disabled={!metadata}
      sx={{ ...COMPACT_FIELD_SX, width: '100%', gridColumn: { xs: '1 / -1', lg: 'auto' } }}
    >
      {(metadata?.presets || []).map((presetOption) => (
        <MenuItem key={presetOption.code} value={presetOption.code.toLowerCase()}>
          {getMetadataLabel('presets', presetOption.code)}
        </MenuItem>
      ))}
    </TextField>

    <TextField
      label={t('breakoutCandidates.filters.minScore')}
      type="number"
      size="small"
      value={filters.min_score}
      onChange={(e) => setFilters((p) => ({ ...p, min_score: Number(e.target.value || 0) }))}
      slotProps={{ inputLabel: { shrink: true } }}
      sx={{ ...COMPACT_FIELD_SX, width: '100%' }}
    />

    <TextField
      select
      label={t('breakoutCandidates.filters.signal')}
      size="small"
      value={filters.signal}
      onChange={(e) => setFilters((p) => ({ ...p, signal: e.target.value }))}
      slotProps={{
        inputLabel: { shrink: true },
        select: {
          displayEmpty: true,
          renderValue: (value) => (value ? getMetadataLabel('signals', value) : t('common.all')),
        },
      }}
      disabled={!metadata}
      sx={{ ...COMPACT_FIELD_SX, width: '100%' }}
    >
      <MenuItem value="">{t('common.all')}</MenuItem>
      {(metadata?.signals || []).map((signalOption) => (
        <MenuItem key={signalOption.label.code} value={signalOption.label.code}>
          {getLocalizedText(signalOption.label, language)}
        </MenuItem>
      ))}
    </TextField>

    <TextField
      select
      label={t('breakoutCandidates.filters.regime')}
      size="small"
      value={filters.market_regime}
      onChange={(e) => setFilters((p) => ({ ...p, market_regime: e.target.value }))}
      slotProps={{
        inputLabel: { shrink: true },
        select: {
          displayEmpty: true,
          renderValue: (value) => (value ? getMetadataLabel('marketRegimes', value) : t('common.all')),
        },
      }}
      disabled={!metadata}
      sx={{ ...COMPACT_FIELD_SX, width: '100%' }}
    >
      <MenuItem value="">{t('common.all')}</MenuItem>
      {(metadata?.market_regimes || []).map((regimeOption) => (
        <MenuItem key={regimeOption.code} value={regimeOption.code}>
          {getLocalizedText(regimeOption, language)}
        </MenuItem>
      ))}
    </TextField>

    <TextField
      label={t('breakoutCandidates.filters.limit')}
      type="number"
      size="small"
      value={filters.limit}
      onChange={(e) => setFilters((p) => ({ ...p, limit: Number(e.target.value || 50) }))}
      slotProps={{ inputLabel: { shrink: true } }}
      sx={{ ...COMPACT_FIELD_SX, width: '100%' }}
    />

    <TextField
      select
      label={t('breakoutCandidates.filters.strong')}
      size="small"
      value={filters.strong_only ? 'ON' : 'OFF'}
      onChange={(e) => setFilters((p) => ({ ...p, strong_only: e.target.value === 'ON' }))}
      slotProps={{ inputLabel: { shrink: true } }}
      sx={{ ...COMPACT_FIELD_SX, width: '100%' }}
    >
      <MenuItem value="ON">{t('breakoutCandidates.filters.on')}</MenuItem>
      <MenuItem value="OFF">{t('breakoutCandidates.filters.off')}</MenuItem>
    </TextField>

    <TextField
      select
      label={t('breakoutCandidates.filters.excludeNone')}
      size="small"
      value={filters.exclude_trigger_none ? 'ON' : 'OFF'}
      onChange={(e) => setFilters((p) => ({ ...p, exclude_trigger_none: e.target.value === 'ON' }))}
      slotProps={{ inputLabel: { shrink: true } }}
      sx={{ ...COMPACT_FIELD_SX, width: '100%' }}
    >
      <MenuItem value="ON">{t('breakoutCandidates.filters.on')}</MenuItem>
      <MenuItem value="OFF">{t('breakoutCandidates.filters.off')}</MenuItem>
    </TextField>

    <Button
      variant="contained"
      size="small"
      onClick={onApply}
      disabled={loading}
      sx={{
        textTransform: 'none',
        fontWeight: 700,
        height: 36,
        px: 2.2,
        width: '100%',
        gridColumn: { xs: '1 / -1', sm: 'span 1', lg: 'auto' },
        boxShadow: 'none',
        bgcolor: C.accent,
        '&:hover': { bgcolor: '#1d44b8', boxShadow: 'none' },
      }}
    >
      {t('common.apply')}
    </Button>
  </Box>
);

BreakoutFiltersBar.propTypes = {
  t: PropTypes.func.isRequired,
  language: PropTypes.string,
  metadata: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  filters: breakoutFiltersShape.isRequired,
  setFilters: PropTypes.func.isRequired,
  getMetadataLabel: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

BreakoutFiltersBar.defaultProps = {
  language: 'en',
  metadata: null,
  loading: false,
};

export default BreakoutFiltersBar;
